import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import { fetchPatientCareContexts, isAbortError } from "./api";
import { PR_COLORS } from "./constants";
import { formatLongDate } from "./formatters";
import SectionDataGrid from "./SectionDataGrid";

/**
 * Sorting runs on the raw field and formatting happens in the cell, so the Date
 * column orders chronologically rather than alphabetically by "12 Jul 2026".
 */
const COLUMNS = [
  {
    field: "visitDateTime",
    headerName: "Date",
    width: 130,
    renderCell: (params) => (
      <Box
        component="span"
        sx={{ fontWeight: 600, color: PR_COLORS.primary, whiteSpace: "nowrap" }}
      >
        {formatLongDate(params.value)}
      </Box>
    ),
  },
  {
    field: "visitDetail",
    headerName: "Visit purpose",
    flex: 1,
    minWidth: 200,
    valueGetter: ({ value }) => value || "Visit purpose",
  },
];

/**
 * Care Contexts are what this Facility has PUBLISHED for the Patient — not the
 * appointments it booked. A Patient can have either without the other, so this
 * section is deliberately separate from Past Appointments and empties
 * independently. No doctor name: the only endpoint carrying one for a care
 * context is doctor-only and broken for every other role.
 */
export default function CareContextsSection({ patientId }) {
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!patientId) return undefined;
    const controller = new AbortController();

    setContexts([]);
    setError("");
    setLoading(true);

    fetchPatientCareContexts({ patientId, signal: controller.signal })
      .then((rows) => {
        if (controller.signal.aborted) return;
        // The grid refuses to render a row without an id. Added here rather than
        // in the API layer so the raw Care Context shape other callers depend on
        // stays untouched; the positional fallback is prefixed so it can never
        // collide with a real reference.
        setContexts(
          rows.map((row, index) => ({
            ...row,
            id: row.reference || `idx-${index}`,
          })),
        );
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.error("Failed to load care contexts:", err);
        setError("Could not load this patient's visit purposes.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [patientId]);

  return (
    <SectionDataGrid
      icon={<HubOutlinedIcon />}
      title="Visit Purposes"
      subtitle="Visit purposes this facility has recorded for this patient."
      rows={contexts}
      columns={COLUMNS}
      loading={loading}
      error={error}
      loadingText="Loading visit purposes…"
      emptyText="No visit purposes recorded for this patient yet."
    />
  );
}
